import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Compass, Users, CalendarHeart, MessageCircle, CircleUserRound } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const items = [
  { to: "/app/descubrir", label: "Descubrir", icon: Compass, tid: "nav-descubrir" },
  { to: "/app/grupos", label: "Grupos", icon: Users, tid: "nav-grupos" },
  { to: "/app/mis-planes", label: "Mis planes", icon: CalendarHeart, tid: "nav-planes" },
  { to: "/app/chats", label: "Chats", icon: MessageCircle, tid: "nav-chats", key: "chats" },
  { to: "/app/perfil", label: "Perfil", icon: CircleUserRound, tid: "nav-perfil" },
];

export default function BottomNav() {
  const [counts, setCounts] = useState({ total: 0, new_matches: 0, unread_messages: 0 });
  const prev = useRef({ new_matches: 0, unread_messages: 0, first: true });
  const location = useLocation();
  const nav = useNavigate();

  useEffect(() => {
    let cancelled = false;
    let interval = null;
    const load = async () => {
      if (document.hidden) return;
      try {
        const { data } = await api.get("/notifications/counts");
        if (cancelled) return;
        const inChats = location.pathname.startsWith("/app/chats");
        if (!prev.current.first && !inChats) {
          if (data.new_matches > prev.current.new_matches) {
            toast.success("¡Hay plan! Tienes un nuevo match", {
              action: { label: "Ver", onClick: () => nav("/app/chats") },
              duration: 6000,
            });
          } else if (data.unread_messages > prev.current.unread_messages) {
            toast("Nuevo mensaje en tu chat", {
              action: { label: "Ver", onClick: () => nav("/app/chats") },
              duration: 5000,
            });
          }
        }
        prev.current = { new_matches: data.new_matches, unread_messages: data.unread_messages, first: false };
        setCounts(data);
      } catch { /* ignore */ }
    };
    load();
    interval = setInterval(load, 15000);
    const onVis = () => { if (!document.hidden) load(); };
    window.addEventListener("focus", onVis);
    document.addEventListener("visibilitychange", onVis);
    return () => { cancelled = true; if (interval) clearInterval(interval); window.removeEventListener("focus", onVis); document.removeEventListener("visibilitychange", onVis); };
  }, [location.pathname, nav]);

  const chatBadge = counts.new_matches + counts.unread_messages + (counts.unseen_likes || 0);

  return (
    <nav
      data-testid="bottom-nav"
      className="fixed bottom-0 left-0 right-0 z-40 flex justify-center pointer-events-none"
    >
      <div className="pointer-events-auto w-full max-w-md mx-3 mb-3 rounded-3xl bg-[#0B0C10]/95 backdrop-blur-xl border border-white/[.16] shadow-2xl px-2 py-2 flex justify-between">
        {items.map((it) => {
          const showBadge = it.key === "chats" && chatBadge > 0;
          return (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={it.tid}
              className={({ isActive }) =>
                `relative flex flex-col items-center justify-center flex-1 py-2 px-1 text-[10px] font-bold transition-colors ${
                  isActive ? "text-white" : "text-[#8E93A3] hover:text-white/80"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span aria-hidden="true" className="absolute -top-2 w-[22px] h-[3px] rounded-b-[3px] ps-gradient"/>
                  )}
                  <it.icon size={20} strokeWidth={1.9} />
                  <span className="mt-1">{it.label}</span>
                  {showBadge && (
                    <span data-testid="chats-badge" className="absolute top-0 right-3 w-2 h-2 rounded-full bg-[#FF6B5E] ring-2 ring-[#0B0C10]"/>
                  )}
                </>
              )}
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
